import React, { useEffect, useState } from 'react'
import Calling from '../../api/Calling';
import { toast } from 'react-toastify';
import Loader from '../../components/Loader';
import { MdDeleteSweep } from 'react-icons/md';
import formatDateTime from '../../components/formatDateTime';
import { FaArrowLeft, FaArrowRight, FaUser } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import MainButton from '../../components/ul/MainButton';
import generateCaptcha from '../../components/captcha';
import { MdOutlineDone } from "react-icons/md";
import { RxCross2 } from "react-icons/rx";
import DeleteButton from '../../components/ul/DeleteButton';
import { LuDot } from 'react-icons/lu';
import NotFound from '../../components/ul/NotFound';
import Navbar from './Navbar';
import { AiOutlineCheckCircle } from 'react-icons/ai';
import { BiSelectMultiple } from 'react-icons/bi';

const UserDelete = () => {
    const [loader, setLoader] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecord, setTotalRecord] = useState(0);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [data, setData] = useState([]);
    const navigate = useNavigate()
    const limit = 50;

    const fetchDataNew = async () => {
        setLoader(true);
        try {
            const response = await Calling.getAllUserDeleteReq(currentPage, limit);
            console.log(response)
            if (response?.status) {
                setData(response?.data || []);
                setTotalPages(response?.totalPage || 1);
                setTotalRecord(response?.totalRecord || 0);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Failed to fetch data');
        } finally {
            setLoader(false);
        }
    };

    useEffect(() => {
        fetchDataNew();
        // eslint-disable-next-line
    }, [currentPage]);

    const handleSelectUser = (id) => {
        if (selectedUsers.includes(id)) {
            setSelectedUsers(selectedUsers.filter((userId) => userId !== id));
        } else {
            setSelectedUsers([...selectedUsers, id]);
        }
    };

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            // Only select pending users (not already deleted)
            const pendingUsers = data.filter(user => !user.status).map(user => user._id);
            setSelectedUsers(pendingUsers);
        } else {
            setSelectedUsers([]);
        }
    };

    const handleUser = (id) => {
        navigate(`/manage/user/all/info/${id}`)
    }

    // Handle single or multiple user accept (delete user accounts)
    const handleUserAccept = async (userIds = null) => {
        const idsToProcess = userIds ? (Array.isArray(userIds) ? userIds : [userIds]) : selectedUsers.map(id => {
            const user = data.find(u => u._id === id);
            return user?.u_id?._id || user?.u_id;
        }).filter(Boolean);

        if (idsToProcess.length === 0) {
            toast.error("Please select at least one user");
            return;
        }

        const content = `You are about to delete ${idsToProcess.length} user account(s). To confirm, please solve this math problem.`;
        const check = generateCaptcha(content);

        if (check) {
            try {
                setLoader(true);
                let body = { id: idsToProcess };

                const response = await Calling.deleteUserreq(body);
                if (response?.status) {
                    toast.success(response?.message || 'User(s) deleted successfully');
                    setSelectedUsers([]);
                    fetchDataNew();
                } else {
                    toast.error(response?.message || 'Failed to delete user(s)');
                }
            } catch (error) {
                console.error('Error deleting users:', error);
                toast.error('Error deleting users');
            } finally {
                setLoader(false);
            }
        } else {
            toast.error("Captcha answer is incorrect", { autoClose: 2000 });
        }
    }

    // Handle single or multiple request rejection (delete from request table)
    const handleDeleteSelected = async (requestIds = null) => {
        const idsToProcess = requestIds ? (Array.isArray(requestIds) ? requestIds : [requestIds]) : selectedUsers;

        if (idsToProcess.length === 0) {
            toast.error("Please select at least one request");
            return;
        }

        const content = `You are about to reject ${idsToProcess.length} delete request(s). To confirm, please solve this math problem.`;
        const check = generateCaptcha(content);

        if (check) {
            try {
                setLoader(true);
                let body = { id: idsToProcess };

                const response = await Calling.deleteUserreqTable(body);
                if (response?.status) {
                    toast.success('Request(s) rejected successfully!');
                    setSelectedUsers([]);
                    fetchDataNew();
                } else {
                    toast.error(response?.message || 'Failed to reject request(s)');
                }
            } catch (error) {
                console.error('Error rejecting requests:', error);
                toast.error('Error rejecting requests');
            } finally {
                setLoader(false);
            }
        } else {
            toast.error("Captcha answer is incorrect", { autoClose: 2000 });
        }
    };

    // Get only pending users count
    const pendingUsersCount = data.filter(user => !user.status).length;
    const selectedPendingUsers = selectedUsers.filter(id => {
        const user = data.find(u => u._id === id);
        return user && !user.status;
    });

    return (
        <>
            <div className="w-full text-main-font px-2 sm:px-3 md:ml-[4.4rem] mt-16">
                <h1 className="text-xl sm:text-2xl font-bold mt-4 text-main-font w-auto">User Management</h1>
                <div className='bg-main-white rounded-xl shadow-lg'>
                    <Navbar selectState={'user'} />
                    <hr className='border-main-border' />

                    {/* Header section */}
                    <div className='flex flex-col sm:flex-row justify-between items-start sm:items-center p-4 sm:p-6 gap-4'>
                        <div>
                            <h1 className='text-xl sm:text-2xl font-bold text-main-font'>
                                User Delete Requests
                            </h1>
                            <p className='text-sm text-main-subfont mt-1'>
                                Total {totalRecord} request(s) • {pendingUsersCount} pending
                            </p>
                        </div>
                    </div>

                    {/* Bulk Actions Bar */}
                    {selectedPendingUsers.length > 0 && (
                        <div className='bg-blue-50 border-b border-blue-200 p-4 flex flex-col sm:flex-row items-center justify-between gap-3'>
                            <div className='flex items-center gap-2'>
                                <BiSelectMultiple size={20} className='text-blue-600' />
                                <span className='text-sm font-medium text-blue-900'>
                                    {selectedPendingUsers.length} user(s) selected
                                </span>
                            </div>
                            <div className='flex gap-2'>
                                <button
                                    onClick={() => handleUserAccept()}
                                    className='flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors text-sm font-medium'
                                >
                                    <MdOutlineDone size={16} />
                                    Accept All ({selectedPendingUsers.length})
                                </button>
                                <button
                                    onClick={() => handleDeleteSelected()}
                                    className='flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors text-sm font-medium'
                                >
                                    <RxCross2 size={16} />
                                    Reject All ({selectedPendingUsers.length})
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Table */}
                    <div className="overflow-x-auto w-full">
                        <table className='min-w-full'>
                            <thead className='bg-gray-50 text-gray-700 uppercase text-xs'>
                                <tr>
                                    <th className='ps-4 sm:ps-6 py-4 text-left rounded-tl-lg'>
                                        <input
                                            type='checkbox'
                                            className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                                            onChange={handleSelectAll}
                                            checked={pendingUsersCount > 0 && selectedPendingUsers.length === pendingUsersCount}
                                        />
                                    </th>
                                    <th className="p-3 text-left font-semibold">#</th>
                                    <th className="p-3 text-left font-semibold">User ID</th>
                                    <th className="p-3 text-left font-semibold">Name</th>
                                    <th className="p-3 text-left font-semibold">Email</th>
                                    <th className="p-3 text-left font-semibold">Platform</th>
                                    <th className="p-3 text-left font-semibold">Status</th>
                                    <th className="p-3 text-left font-semibold">Request Date</th>
                                    <th className="p-3 text-left font-semibold rounded-tr-lg">Actions</th>
                                </tr>
                            </thead>
                            {loader ? (
                                <tbody>
                                    <tr>
                                        <td colSpan={10} className="text-center py-8">
                                            <Loader loader={loader} />
                                        </td>
                                    </tr>
                                </tbody>
                            ) : (
                                <tbody className='divide-y divide-gray-200'>
                                    {data.length > 0 ?
                                        data.map((user, index) => (
                                            <tr key={user._id} className="hover:bg-gray-50 transition-colors">
                                                <td className="ps-4 sm:ps-6 py-3">
                                                    <input
                                                        type="checkbox"
                                                        className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                                                        checked={selectedUsers.includes(user._id)}
                                                        onChange={() => handleSelectUser(user._id)}
                                                        disabled={user.status}
                                                    />
                                                </td>
                                                <td className="p-3 text-sm">{index + 1 + (currentPage - 1) * limit}</td>
                                                <td className="p-3 text-sm font-mono text-gray-600">{user?.u_id?._id || user?.u_id}</td>
                                                <td className="p-3">
                                                    <div className="flex items-center gap-2">
                                                        {user?.u_id?.u_profile && (
                                                            <img
                                                                src={user.u_id.u_profile}
                                                                alt={user.u_id.u_name}
                                                                className="w-8 h-8 rounded-full object-cover"
                                                                onError={(e) => {
                                                                    e.target.style.display = 'none';
                                                                }}
                                                            />
                                                        )}
                                                        <span className="text-sm font-medium">{user?.u_id?.u_name || 'N/A'}</span>
                                                    </div>
                                                </td>
                                                <td className="p-3 text-sm text-gray-600">{user?.u_id?.u_email || 'N/A'}</td>
                                                <td className="p-3 text-sm">
                                                    <span className={`px-3 py-1 text-xs font-medium rounded-full ${user?.platform === 'app' ? 'bg-purple-100 text-purple-700' :
                                                            user?.platform === 'web' ? 'bg-blue-100 text-blue-700' :
                                                                'bg-gray-100 text-gray-700'
                                                        }`}>
                                                        {user?.platform?.toUpperCase()}
                                                    </span>
                                                </td>
                                                <td className="p-3">
                                                    {user?.status ?
                                                        <span className='px-3 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700 flex items-center w-max gap-1'>
                                                            <AiOutlineCheckCircle size={14} /> Processed
                                                        </span> :
                                                        <span className='px-3 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-700 flex items-center w-max gap-1'>
                                                            <LuDot size={16} />Pending
                                                        </span>
                                                    }
                                                </td>
                                                <td className="p-3 text-sm text-gray-600">{formatDateTime(user.createdAt)}</td>
                                                <td className="p-3">
                                                    {user?.status ?
                                                        <span className='px-3 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-600'>
                                                            Completed
                                                        </span>
                                                        :
                                                        <div className="flex items-center gap-2">
                                                            <button
                                                                onClick={() => handleUser(user?.u_id?._id || user?.u_id)}
                                                                className="p-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                                                                title="View User Details"
                                                            >
                                                                <FaUser size={14} />
                                                            </button>
                                                            <button
                                                                onClick={() => handleUserAccept(user?.u_id?._id || user?.u_id)}
                                                                className='p-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors'
                                                                title="Accept & Delete User Account"
                                                            >
                                                                <MdOutlineDone size={14} />
                                                            </button>
                                                            <button
                                                                onClick={() => handleDeleteSelected(user?._id)}
                                                                className='p-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors'
                                                                title="Reject Request"
                                                            >
                                                                <RxCross2 size={14} />
                                                            </button>
                                                        </div>
                                                    }
                                                </td>
                                            </tr>
                                        ))
                                        : (
                                            <tr>
                                                <td colSpan="9" className="py-8"><NotFound /></td>
                                            </tr>
                                        )
                                    }
                                </tbody>
                            )}
                        </table>
                    </div>

                    {/* Pagination */}
                    <div className="flex flex-col sm:flex-row justify-between items-center p-6 border-t border-gray-200">
                        <div className="text-sm text-gray-600 mb-4 sm:mb-0">
                            Showing {data.length > 0 ? ((currentPage - 1) * limit) + 1 : 0} to {Math.min(currentPage * limit, totalRecord)} of {totalRecord} entries
                        </div>
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                                disabled={currentPage === 1}
                                className={`px-4 py-2 flex items-center gap-2 text-sm rounded-lg transition-colors ${currentPage === 1
                                        ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                                        : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
                                    }`}
                            >
                                <FaArrowLeft size={12} /> Previous
                            </button>

                            <div className="flex items-center gap-2">
                                <span className="text-sm text-gray-600">Page</span>
                                <span className="px-3 py-1 bg-blue-500 text-white rounded-lg text-sm font-medium">
                                    {currentPage}
                                </span>
                                <span className="text-sm text-gray-600">of {totalPages}</span>
                            </div>

                            <button
                                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                                disabled={currentPage === totalPages}
                                className={`px-4 py-2 flex items-center gap-2 text-sm rounded-lg transition-colors ${currentPage === totalPages
                                        ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                                        : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
                                    }`}
                            >
                                Next <FaArrowRight size={12} />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

export default UserDelete